import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import {
  Card,
  CardHeader,
  Divider,
  CardContent,
  FormControl,
  InputLabel,
  OutlinedInput,
  CardActions,
  Button,
  TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { useSnackbar } from "../../../context/SnackbarContext";
import { useUpdateUserInfo } from "../../../api/user/use-update-user";
import { useTimezone } from "../../../context/TimezoneContext";
import TimezoneSelector from "./TimezoneSelector";

export function AccountDetailsForm() {
  const user = useSelector((state: RootState) => state.auth.user);
  const updateUserInfoMutation = useUpdateUserInfo();
  const { showSnackbar } = useSnackbar();

  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [username, setUsername] = useState(user?.username || "");

  const [selectedTimezone, setSelectedTimezone] = useState(
    user?.timezone || "UTC"
  );

  const { setTimezone } = useTimezone();

  useEffect(() => {
    if (user?.timezone) {
      setSelectedTimezone(user.timezone);
    }
  }, [user]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (firstName.trim().length < 2) {
      showSnackbar("First name must be at least 2 characters", "error");
      return;
    }
    if (lastName.trim().length < 2) {
      showSnackbar("Last name must be at least 2 characters", "error");
      return;
    }

    const userData = {
      data: {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        username: username.trim(),
        timezone: selectedTimezone,
      },
      userId: user?.id,
    };

    if (user?.id) {
      updateUserInfoMutation.mutate(
        { ...userData, userId: user.id },
        {
          onSuccess: () => {
            setTimezone(selectedTimezone);
            showSnackbar("User info updated successfully", "success");
          },
          onError: (error: any) => {
            const errorMsg = error?.message || "Failed to update user info";
            showSnackbar(errorMsg, "error");
          },
        }
      );
    } else {
      showSnackbar("User ID is missing", "error");
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader subheader="The information can be edited" title="Profile" />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>First name</InputLabel>
                <OutlinedInput
                  label="First name"
                  name="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Last name</InputLabel>
                <OutlinedInput
                  label="Last name"
                  name="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Email address</InputLabel>
                <OutlinedInput
                  defaultValue={user?.email ?? ""}
                  label="Email address"
                  name="email"
                  readOnly
                  disabled
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Username</InputLabel>
                <OutlinedInput
                  label="Username"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TimezoneSelector
                value={selectedTimezone}
                onChange={setSelectedTimezone}
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            type="submit"
            disabled={updateUserInfoMutation.isPending}
          >
            {updateUserInfoMutation.isPending ? "Saving..." : "Save details"}
          </Button>
        </CardActions>
      </Card>
    </form>
  );
}
